import React, { useCallback, useRef, useState } from "react";
import styled from "@emotion/styled";
import { fontP3Regular } from "src/design-system/styles/fonts";
import { usePageSet } from "src/hooks/pageSet";
import { css } from "@emotion/react";
import { compassColors } from "src/utils/styling";
import ClinicalHeroSection from "./ClinicalHeroSection";
import {
  breakpointDesktopOnly,
  breakpointWideDesktopOnly,
} from "src/design-system/styles/breakpoints";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { usePageView } from "src/hooks/tracking";
import TheScience from "./ClinicalIntroContent/TheScience";
import { useInView } from "react-intersection-observer";
import ClinicalDescription from "./ClinicalDescription";
import { usePageBackground } from "src/hooks/usePageBackground";
import i18n from "@i18n";
import {
  ClinicalFAQ,
  HowItWorks,
  ImproveLongevity,
  Meds,
  OurApproach,
  Reviews,
} from "./ClinicalIntroContent";

import Disclaimer from "./ClinicalDisclaimer";
import {
  isIdealWeightOnFreemiumLPEligible,
  isLDSAudienceCampaign,
} from "src/utils/userSegment/features";
import { ClinicalWeightLossGoal } from "@components/intro/ClinicalWeightLossGoal";
import { FreemiumIdealWeightConfirmation } from "@components/intro/FreemiumIdealWeight";
import { LDSHopingToAchieve } from "@components/intro/LDSHopingToAchieve";
import goto from "@pageDefinitions/goto";
import { isFreemiumClinicalRoute } from "@utils/userSegment";
import { routeConstants } from "@utils/constants";

type SectionId =
  | "hero"
  | "meds"
  | "ourApproach"
  | "howItWorks"
  | "theScience"
  | "reviews"
  | "faq";

type SectionVisibility = {
  [id in SectionId]: boolean;
};

const LandingContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  object-fit: cover;
  position: relative;
`;

const StickyContentBar = styled.div`
  width: 100%;
  background-color: ${compassColors.white};
  height: 90px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: sticky;
  align-self: flex-start;
  top: 0;
  z-index: 4;
  overflow-x: scroll;
  white-space: nowrap;
  @media ${breakpointDesktopOnly} {
    justify-content: center;
    border-radius: 50px 50px 0 0;
    margin-top: -40px;
  }
`;

const ContentMenuButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{
  selected?: boolean;
}>`
  ${fontP3Regular}
  color: ${compassColors.grey3};
  margin: 0;
  border: none;
  background: none;
  box-sizing: border-box;
  padding: 0 16px;
  height: 100%;
  ${({ selected }) =>
    selected &&
    css`
      color: ${compassColors.lagoon};
      font-weight: 500;
      text-decoration: solid underline 2px;
    `}
`;

const ClinicalHeroSectionContainer = styled.div`
  width: 100%;
`;

const MedsContainer = styled.div`
  width: 100%;
  background-color: ${compassColors.white};
`;

const OurApproachContainer = styled.div`
  background-color: ${compassColors.white};
  margin-top: 56px;
  border-radius: 0 80px 0 0;
  @media ${breakpointDesktopOnly} {
    margin: 60px 80px 60px 80px;
    border-radius: 40px;
  }
  @media ${breakpointWideDesktopOnly} {
    margin: 60px 120px 60px 120px;
  }
`;

const HowItWorksContainer = styled.div`
  width: 100%;
  background-color: ${compassColors.skyLite};
`;

const TheScienceContainer = styled.div`
  width: 100%;
  background-color: ${compassColors.skyLite};
  @media ${breakpointDesktopOnly} {
    background-color: unset;
    margin-top: 80px;
    width: calc(100% - 80px);
    margin-right: auto;
  }
`;

const FAQContainer = styled.div`
  width: 100%;
  background-color: ${compassColors.white};
`;

const ImproveLongevityContainer = styled.div`
  width: 100%;
  background-color: ${compassColors.skyLite};
`;

const AboveFold = styled.div`
  min-height: calc(100vh - 75px);
  // this prevents desktop chrome from using -webkit-fill-available which it does
  // not use properly.
  @supports (-webkit-touch-callout: none):  {
    min-height: "-webkit-fill-available";
  }
`;

const ReviewsContainer = styled.div`
  background-color: ${compassColors.skyLite};
  width: 100%;
`;

let scrollDebounceTimeout: NodeJS.Timeout;

export default function ClinicalIntro() {
  const { gotoNextPage } = usePageSet();
  const isLDSAudience = isLDSAudienceCampaign();

  usePageBackground(compassColors.offWhite);

  const [stickyMenuSelectedEntry, setStickyMenuSelectedEntry] = useState(0);
  const [visibleSection, setVisibleSection] = useState("");
  const heroRef = useRef<null | HTMLDivElement>(null);
  const medsRef = useRef<null | HTMLDivElement>(null);
  const ourApproachRef = useRef<null | HTMLDivElement>(null);
  const howItWorksRef = useRef<null | HTMLDivElement>(null);
  const theScienceRef = useRef<null | HTMLDivElement>(null);
  const reviewsRef = useRef<null | HTMLDivElement>(null);
  const faqRef = useRef<null | HTMLDivElement>(null);
  const stickyMenuRef = useRef<null | HTMLDivElement>(null);

  const stickyMenuEntries = [
    {
      text: i18n.t("introClinical:stickyMenu:meds"),
      ref: medsRef,
      eventProperty: "meds",
    },
    {
      text: i18n.t("introClinical:stickyMenu:ourApproach"),
      ref: ourApproachRef,
      eventProperty: "ourApproach",
    },
    {
      text: i18n.t("introClinical:stickyMenu:howItWorks"),
      ref: howItWorksRef,
      eventProperty: "howItWorks",
    },
    {
      text: i18n.t("introClinical:stickyMenu:theScience"),
      ref: theScienceRef,
      eventProperty: "theScience",
    },
    {
      text: i18n.t("introClinical:stickyMenu:reviews"),
      ref: reviewsRef,
      eventProperty: "reviews",
    },
    {
      text: i18n.t("introClinical:stickyMenu:faq"),
      ref: faqRef,
      eventProperty: "FAQ",
    },
  ];

  const initialState = {
    hero: false,
    meds: false,
    ourApproach: false,
    howItWorks: false,
    theScience: false,
    reviews: false,
    faq: false,
  };

  const [sectionVisibilityConfig, setSectionVisibilityConfig] =
    useState<SectionVisibility>(initialState);

  const onChangeVisibility = (inView: boolean, entry, sectionId: SectionId) => {
    const sectionsVisibilityCopy = { ...sectionVisibilityConfig };
    sectionsVisibilityCopy[sectionId] = inView;
    setSectionVisibilityConfig(sectionsVisibilityCopy);
    updateMenuHighlightOnVisibilityChange(sectionsVisibilityCopy);
  };

  async function gotoWeightLandingPage() {
    goto.landing(
      false,
      isFreemiumClinicalRoute()
        ? routeConstants.freemiumWeight
        : routeConstants.direct
    );
  }

  function scrollNavBarIfNeeded(menuIdx: number) {
    if (
      stickyMenuRef.current &&
      stickyMenuRef.current.scrollWidth !== stickyMenuRef.current.clientWidth
    ) {
      if (menuIdx <= 2 && stickyMenuRef.current.scrollLeft !== 0) {
        stickyMenuRef.current.scrollLeft = 0;
      } else if (
        menuIdx > 2 &&
        stickyMenuRef.current.scrollLeft !==
          stickyMenuRef.current.scrollWidth - stickyMenuRef.current.clientWidth
      ) {
        stickyMenuRef.current.scrollLeft =
          stickyMenuRef.current.scrollWidth - stickyMenuRef.current.clientWidth;
      }
    }
  }

  function updateMenuHighlightOnVisibilityChange(
    visibleSections: SectionVisibility
  ) {
    const visibleSectionsArray = Object.keys(visibleSections);
    for (let i = 0; i < visibleSectionsArray.length; i++) {
      const currentSection = visibleSectionsArray[i];
      if (visibleSections[currentSection]) {
        if (currentSection !== visibleSection) {
          setVisibleSection(currentSection);
          trackBuyflowEvent("BuyflowPageScrolled", {
            pageId: "noomMedLP",
            sectionName:
              i === 0 ? "topOfPage" : stickyMenuEntries[i - 1].eventProperty,
          });

          const actualMenuEntry = Math.max(i - 1, 0);
          if (actualMenuEntry !== stickyMenuSelectedEntry) {
            setStickyMenuSelectedEntry(actualMenuEntry);

            if (scrollDebounceTimeout) {
              clearTimeout(scrollDebounceTimeout);
            }
            scrollDebounceTimeout = setTimeout(
              () => scrollNavBarIfNeeded(actualMenuEntry),
              300
            );
          }
        }
        break;
      }
    }
  }

  const getInViewConfig = (sectionId: SectionId) => ({
    rootMargin: "-90px 0px 0px 0px",
    onChange: (inView: boolean, entry) => {
      onChangeVisibility(inView, entry, sectionId);
    },
  });

  const [inViewHeroRef] = useInView(getInViewConfig("hero"));
  const [inViewMedsRef] = useInView(getInViewConfig("meds"));
  const [inViewOurApproachRef] = useInView(getInViewConfig("ourApproach"));
  const [inViewHowItWorksRef] = useInView(getInViewConfig("howItWorks"));
  const [inViewTheScienceRef] = useInView(getInViewConfig("theScience"));
  const [inViewReviewsRef] = useInView(getInViewConfig("reviews"));
  const [inViewFAQRef] = useInView(getInViewConfig("faq"));

  const setInViewRefs = useCallback((node, generalRef, inViewRef) => {
    const newInViewRef = generalRef;
    newInViewRef.current = node;
    inViewRef(node);
  }, []);

  usePageView("clinicalLanding");

  const barHeight = "89px";

  if (isLDSAudience) {
    return (
      <AboveFold>
        <LandingContainer>
          <ClinicalHeroSectionContainer
            ref={(node) => setInViewRefs(node, heroRef, inViewHeroRef)}
          >
            <ClinicalHeroSection
              gotoNextPage={gotoNextPage}
              gotoWeightLandingPage={gotoWeightLandingPage}
              isLDSAudience
            >
              <LDSHopingToAchieve />
            </ClinicalHeroSection>
          </ClinicalHeroSectionContainer>
        </LandingContainer>
      </AboveFold>
    );
  }

  return (
    <>
      <AboveFold>
        <LandingContainer>
          <ClinicalHeroSectionContainer
            ref={(node) => setInViewRefs(node, heroRef, inViewHeroRef)}
          >
            <ClinicalHeroSection
              gotoNextPage={gotoNextPage}
              gotoWeightLandingPage={gotoWeightLandingPage}
            >
              {isIdealWeightOnFreemiumLPEligible() ? (
                <FreemiumIdealWeightConfirmation />
              ) : (
                <ClinicalWeightLossGoal />
              )}
            </ClinicalHeroSection>
          </ClinicalHeroSectionContainer>
        </LandingContainer>
      </AboveFold>
      <LandingContainer style={{ background: compassColors.offWhite }}>
        <ClinicalDescription gotoNextPage={gotoNextPage} />

        <StickyContentBar ref={stickyMenuRef}>
          {stickyMenuEntries.map((entry, idx) => (
            <ContentMenuButton
              key={entry.eventProperty}
              selected={idx === stickyMenuSelectedEntry}
              onClick={() => {
                trackBuyflowEvent("BuyflowPageScrolled", {
                  pageId: "noomMedLP",
                  sectionName: entry.eventProperty,
                });
                if (entry?.ref?.current) {
                  const entryObj = entry;
                  entryObj.ref.current.style.scrollMargin = barHeight;
                  entryObj.ref.current.scrollIntoView();
                }
              }}
            >
              {entry.text}
            </ContentMenuButton>
          ))}
        </StickyContentBar>
        <MedsContainer
          ref={(node) => setInViewRefs(node, medsRef, inViewMedsRef)}
        >
          <Meds gotoNextPage={gotoNextPage} />
        </MedsContainer>
        <OurApproachContainer
          ref={(node) =>
            setInViewRefs(node, ourApproachRef, inViewOurApproachRef)
          }
        >
          <OurApproach gotoNextPage={gotoNextPage} />
        </OurApproachContainer>
        <HowItWorksContainer
          ref={(node) =>
            setInViewRefs(node, howItWorksRef, inViewHowItWorksRef)
          }
        >
          <HowItWorks gotoNextPage={gotoNextPage} />
        </HowItWorksContainer>
        <TheScienceContainer
          ref={(node) =>
            setInViewRefs(node, theScienceRef, inViewTheScienceRef)
          }
        >
          <TheScience gotoNextPage={gotoNextPage} />
        </TheScienceContainer>
        <ReviewsContainer
          ref={(node) => setInViewRefs(node, reviewsRef, inViewReviewsRef)}
        >
          <Reviews />
        </ReviewsContainer>
        <ImproveLongevityContainer>
          <ImproveLongevity gotoNextPage={gotoNextPage} />
        </ImproveLongevityContainer>
        <FAQContainer ref={(node) => setInViewRefs(node, faqRef, inViewFAQRef)}>
          <ClinicalFAQ />
        </FAQContainer>
        <Disclaimer />
      </LandingContainer>
    </>
  );
}
